/**
 * Interface-string translation.
 *
 *   g.lang                    - the user's language, default 'en' (the base
 *                               language = the language the code is written in).
 *                               Set by the app via g.init({ lang: 'ro' }).
 *   g.translate(text, vars?)  - translation of `text` for g.lang, with optional
 *                               {placeholder} interpolation.
 *   g.setTranslations(map)    - load the { source: translation } dictionary for
 *                               the current (non-base) language. The framework
 *                               emits ONE file per language and injects it before
 *                               app.js; the base language needs no file.
 *
 * Lookup: the SOURCE string is the key (gettext-style). On the base language, or
 * when a string is missing from the dictionary, the source is returned as-is -
 * so an untranslated string always renders in readable English, never a blank.
 *
 * Interpolation: placeholders are {name}; the WHOLE templated string is one
 * translation key (never translate sentence pieces - word order differs between
 * languages). Escape literal braces by doubling: {{ -> {  and  }} -> }, so a
 * string can display a literal "{name}". Interpolation runs on both base and
 * translated strings.
 *   g.translate('Hi {name}, use {{name}} to insert it', { name: 'Ana' })
 *     -> 'Hi Ana, use {name} to insert it'
 *
 * Submit-for-translation: when g.settings.submitForTranslation is true and a
 * string is missing for a non-base language, the phrase is POSTed once (deduped)
 * to g.settings.translationEndpoint so the backend can register it for
 * translation. Fire-and-forget; the source is returned meanwhile.
 *
 * House rule: every INTERFACE string in our libraries/components goes through
 * g.translate() ("Loading...", "No results", aria labels, ...). Data coming
 * from the API is NEVER translated; business/system error messages (error.*)
 * AND server validation results (data.fields / data.message) arrive already
 * translated — the API/DB compose them for the request language carried by
 * the X-Genus-Lang header g.fetch attaches (i18n.md).
 */
import { settings } from './core.js';

export const DEFAULT_LANG = 'en';
const DEFAULT_ENDPOINT = '/frm/translations/register'; // the framework route (i18n.md), in every install
const SUBMIT_STORE_KEY = 'genus-i18n-submitted';

// { source: translation } for the current non-base language. Empty on the base
// language (or before a file is loaded) -> everything falls back to the source.
let dict = {};

export function setTranslations(map) {
	dict = map && typeof map === 'object' ? map : {};
}

const baseLang = () => settings.baseLang || DEFAULT_LANG;
const currentLang = () => (window.g && window.g.lang) || settings.lang || DEFAULT_LANG;

// Fill {name} from vars and unescape {{ }} -> { }. Runs in one pass so an
// escaped brace never gets mistaken for a placeholder.
function interpolate(str, vars) {
	if (str.indexOf('{') === -1) return str; // nothing to do (the common case)
	return str.replace(/\{\{|\}\}|\{(\w+)\}/g, (match, key) => {
		if (match === '{{') return '{';
		if (match === '}}') return '}';
		return vars && Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match;
	});
}

// Deduped set of already-submitted phrases, mirrored to sessionStorage so a
// reload does not re-submit the same strings.
let submitted = null;
function submittedSet() {
	if (submitted) return submitted;
	submitted = new Set();
	try { JSON.parse(sessionStorage.getItem(SUBMIT_STORE_KEY) || '[]').forEach((k) => submitted.add(k)); } catch (e) {}
	return submitted;
}

function submitForTranslation(phrase, lang) {
	if (!settings.submitForTranslation) return;
	const set = submittedSet();
	const key = lang + ' ' + phrase;
	if (set.has(key)) return;
	set.add(key);
	try { sessionStorage.setItem(SUBMIT_STORE_KEY, JSON.stringify([...set])); } catch (e) {}
	if (window.g && window.g.fetch) {
		window.g.fetch(settings.translationEndpoint || DEFAULT_ENDPOINT, { phrase, lang }, { progress: false });
	}
}

export function translate(text, vars) {
	if (text == null) return text;
	let out = text;
	const lang = currentLang();
	if (lang !== baseLang()) {
		if (Object.prototype.hasOwnProperty.call(dict, text)) out = dict[text];
		else submitForTranslation(text, lang);
	}
	return interpolate(out, vars);
}
